import { get, post } from '../utils/networking';

export type ProcessStep = 'SURVEY' | 'RESULTS' | 'ACTION_PLAN' | 'ACTION_PLAN_RESULT';
/** A survey that closed with too few answers stopped the process at its first step */
export type StepState = 'done' | 'current' | 'notStarted' | 'stopped';
export type DeliveryChannel = 'TEAMS' | 'SLACK' | 'EMAIL' | 'SMS';
/** Why the team leader cannot send a reminder now */
export type ReminderBlock = 'closed' | 'recentlySent' | 'comingSoon';

export type Channel = { channel: DeliveryChannel; name: string | null };

export type ActivityType =
  | 'SURVEY_SENT'
  | 'REMINDER_SENT'
  | 'DEADLINE_EXTENDED'
  | 'TOO_FEW_RESPONSES'
  | 'RESULTS_READY'
  | 'RESULT_SHARED'
  | 'ACTION_PLAN_INVITATION_SENT'
  | 'ACTION_PLAN_SUMMARY_SENT'
  | 'ACTION_PLAN_REMINDER_SENT';

export type Activity = {
  type: ActivityType;
  at: string;
  /** Empty for what happened before Trustpulse kept track of the channels */
  channels: Channel[];
  failedChannels: Channel[];
  responses?: number;
  expected?: number;
  deadline?: string;
  /** The team leader asked for it, rather than Trustpulse sending it by itself */
  byTeamLeader: boolean;
};

export type NowCard =
  | {
      step: 'SURVEY';
      responses: number;
      expected: number;
      /** How many answers the result needs; null when the team size is unknown */
      requiredResponses: number | null;
      closesAt: string;
      deadlineExtended: boolean;
      remindsBeforeDeadline: boolean;
      nextReminderAt: string | null;
      lastReminderAt: string | null;
      /** When the team leader can remind the team again, while a reminder went out within a day */
      remindAgainAt: string | null;
      /** Null when the team leader can send a reminder now */
      reminderBlockedBy: ReminderBlock | null;
    }
  | {
      step: 'RESULTS';
      tooFewResponses: boolean;
      resultsReady: boolean;
      analysisFailed: boolean;
      responses: number;
      expected: number;
      requiredResponses: number | null;
      closedAt: string;
      resultSharedAt: string | null;
      resultSharesAt: string | null;
      nextSurveyAt: string | null;
    }
  | {
      step: 'ACTION_PLAN';
      contributors: number;
      teamSize: number;
      contributorsNeeded: number;
      invitationSentAt: string | null;
      invitationSendsAt: string | null;
      /** When Trustpulse invites the team by itself, while it has not yet */
      invitationExpectedAt: string | null;
      /** Trustpulse tried to invite the team, but no channel got it, and nothing tries again */
      invitationNotReached: boolean;
      summary: SummaryState;
      lastReminderAt: string | null;
      /** When the team leader can remind the team again, while a reminder went out within a day */
      remindAgainAt: string | null;
      /** Null when the team leader can remind the team of the action plan now */
      reminderBlockedBy: ReminderBlock | null;
    }
  | {
      step: 'ACTION_PLAN_RESULT';
      contributors: number;
      teamSize: number;
      summarySentAt: string | null;
      nextSurveyAt: string | null;
    };

/** Where the summary of the action plan is while the team is in the action plan step */
export type SummaryState =
  | { state: 'waitingForContributors'; waitsUntil: string | null }
  | { state: 'expected'; at: string }
  | { state: 'preparing' }
  | { state: 'queued'; at: string | null }
  | { state: 'notSent' };

export type Chosen = { statement: string; count: number };

/** What the team chose, given once the summary has gone to the team */
export type TeamSaid = {
  commitments: Chosen[];
  expectations: Chosen[];
};

/** What each step came to, for the line under it */
export type ProcessTotals = {
  responses: number;
  expected: number;
  contributors: number;
  teamSize: number;
  resultSharedAt: string | null;
  summarySentAt: string | null;
  nextSurveyAt: string | null;
};

export type SurveyProcess = {
  survey: { id: number; uuid: string; title: string; startedAt: string };
  /** Which survey of the team this is. The first is round 1 */
  round: number;
  /** The round is over once the summary of the action plan has gone to the team */
  completed: boolean;
  totals: ProcessTotals;
  step: ProcessStep;
  steps: { step: ProcessStep; state: StepState }[];
  tooFewResponses: boolean;
  now: NowCard;
  activity: Activity[];
  actionPlan: { focusArea: string; said: TeamSaid | null } | null;
  /** Trustpulse did not send the summary, so the team leader can */
  canSendSummary: boolean;
};

/** The newest psychological safety survey of the team, or nothing when the team has none */
export const getSurveyProcess = async (teamId: number): Promise<SurveyProcess | null> =>
  (await get<SurveyProcess | ''>(`/api/dashboard/team/${teamId}/survey-process`)) || null;

/** Reminds the team to answer now. It goes out within the working hours of the team */
export const sendSurveyReminder = (teamId: number) =>
  post<void>(`/api/dashboard/team/${teamId}/survey-process/reminder`, {});

/** Sends the summary of the action plan to the team, when Trustpulse did not */
export const sendActionPlanSummary = (teamId: number) =>
  post<void>(`/api/dashboard/team/${teamId}/survey-process/summary`, {});

/** Reminds the team to make the action plan. It goes out within the working hours of the team */
export const sendActionPlanReminder = (teamId: number) =>
  post<void>(`/api/dashboard/team/${teamId}/survey-process/action-plan-reminder`, {});
